import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Paper,
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormGroup,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  PlayCircle,
  LocationOn,
  CurrencyRupee,
  Public,
  ToggleOn,
  ToggleOff,
  ExpandMore,
  ExpandLess,
  Info,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useAxios from "../useAxios";
import ModernLoder, {
  CategoryDetailsLoader,
  RegistrationIdeasLoader,
  ProductDetailLoader,
  ProductCategoriesLoader,
  ProductDetailSkeleton,
} from "./ModernLoder";

// Custom styled components
const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  borderRadius: "8px",
  padding: "10px 20px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    boxShadow: `0 4px 12px ${theme.palette.primary.light}`,
  },
}));

const CompactCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
}));

const StatusPill = styled(Chip)(({ theme, status }) => ({
  backgroundColor:
    status === "on" ? theme.palette.success.light : theme.palette.error.light,
  color:
    status === "on" ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600,
  padding: "4px 8px",
  borderRadius: "12px",
  "& .MuiChip-label": {
    padding: "0 4px",
  },
}));
const PricingCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
  transition: "all 0.3s ease",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  },
}));
const ProductDetailView = ({ product, onBack }) => {
  const axiosData = useAxios();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [cityStatus, setCityStatus] = useState({});
  const [openCityDialog, setOpenCityDialog] = useState(false);
  const [selectedOffCities, setSelectedOffCities] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    deliverables: false,
    requirements: false,
    materials: false,
    pricing: false,
    pricing1: false,
    pricing2: false,
    pricing3: false,
    pricing4: false,
    pricing5: false,
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    if (product?.status) {
      setCityStatus(product.status);
    } else {
      const defaultStatus = {};
      product.price.forEach((p) => {
        const city = Object.keys(p)[0];
        defaultStatus[city] = "on";
      });
      setCityStatus(defaultStatus);
    }
  }, [product]);

  const isFullyOn = Object.values(cityStatus).every((v) => v === "on");
  const isFullyOff = Object.values(cityStatus).every((v) => v === "off");

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCityToggle = async (city) => {
    const newStatus = cityStatus[city] === "on" ? "off" : "on";
    const updatedStatus = { ...cityStatus, [city]: newStatus };
    try {
      setLoading(true);
      const response = await axiosData.put(`editProductDetails`, {
        _id: product._id,
        status: updatedStatus,
      });
      if (response.data) {
        setCityStatus(updatedStatus);
        product.status = updatedStatus;
      }
    } catch (error) {
      console.error("City status update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalToggle = async () => {
    const newStatus = isFullyOn ? "off" : "on";
    const updatedStatus = Object.keys(cityStatus).reduce((acc, city) => {
      acc[city] = newStatus;
      return acc;
    }, {});

    try {
      setLoading(true);
      const response = await axiosData.put(`editProductDetails`, {
        _id: product._id,
        status: updatedStatus,
      });
      if (response.data) {
        setCityStatus(updatedStatus);
        product.status = updatedStatus;
      }
    } catch (error) {
      console.error("Global status update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!product) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          p: { xs: 2, md: 4 },
          bgcolor: "background.default",
          borderRadius: "20px",
        }}
      >
        <ProductDetailSkeleton onBack={onBack} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{
          color: "text.primary",
          mb: 4,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          border: "1.5px solid", // Thicker border
          transition: "all 0.3s ease",
          borderColor: "#d32f2f", // Red border color
          "&:hover": {
            backgroundColor: "action.hover",
            transform: "translateX(-4px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderColor: "#d32f2f", // Keep red on hover
            "& .MuiSvgIcon-root": {
              transform: "translateX(-2px)",
            },
          },
          "&:active": {
            transform: "translateX(-2px) scale(0.98)",
          },
        }}
      >
        Back to Products
      </Button>

      <Grid container spacing={2}>
        {/* Main Content Column */}
        <Grid item xs={12} md={12}>
          {/* Product Header */}
          <CompactCard sx={{ mb: 2 }}>
            <CardContent>
              {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h4" fontWeight={800} gutterBottom color="primary">
                    {product.produt_name}
                  </Typography>
                  
                  <Chip
                    label={product.productCategory}
                    color="primary"
                    size="small"
                    variant="filled"
                    sx={{  mr: 2,
                  fontWeight: 600,
                  fontSize: "0.875rem", }}
                  />
                </Box>
                
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center',textAlign:"center",justifyContent:"center",flexDirection:"coloumn" }}>
                  <Tooltip title={isFullyOn ? "All cities active" : isFullyOff ? "All cities inactive" : "Partial availability"}>
                   <Typography variant="subtitle1" color={isFullyOn ? "success.main" : "text.disabled"}sx={{ display: 'flex', alignItems: 'center',textAlign:"center",justifyContent:"center"}}>
                    <Public color={isFullyOn ? "success" : isFullyOff ? "error" : "warning"} />{isFullyOn ? "All Cities Active" : isFullyOff ? "All Cities Inactive" : "Partial Availability"}
                   </Typography>
                  </Tooltip>
                  </Box>
                  <Button
                    variant="outlined"
                    color={isFullyOn ? "error" : "success"}
                    startIcon={isFullyOn ? <ToggleOff /> : <ToggleOn />}
                    onClick={handleGlobalToggle}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '20px',
                    px: 3,
                    py: 1
                    }}
                  >
                    {isFullyOn ? "Disable All" : "Enable All"}
                  </Button>
                </Stack>
              </Box> */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" }, // Stack on mobile, row on larger screens
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                  width: "100%",
                }}
              >
                {/* Left side - Product name and category */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: { xs: "flex-start" },
                  }}
                >
                  <Typography
                    variant="h4"
                    gutterBottom
                    color="primary"
                    sx={{
                      fontWeight: { xs: 800, sm: 800, md: 800 }, // Bold on sm+ screens
                      fontSize: { xs: "1.5rem", sm: "2rem", md: "2rem" }, // Same small size for all screen
                    }}
                  >
                    {product.produt_name}
                  </Typography>

                  <Chip
                    label={product.productCategory}
                    color="primary"
                    size="small"
                    variant="filled"
                    sx={{
                      mr: 2,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      alignSelf: { xs: "flex-start", sm: "flex-start" },
                    }}
                  />
                </Box>

                {/* Right side - Status and toggle button */}
                <Stack
                  direction={{ xs: "column", sm: "row" }} // Stack vertically on mobile, row on larger screens
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    mt: { xs: 1, sm: 0 },
                  }}
                >
                  <Tooltip
                    title={
                      isFullyOn
                        ? "All cities active"
                        : isFullyOff
                        ? "All cities inactive"
                        : "Partial availability"
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      color={isFullyOn ? "success.main" : "text.disabled"}
                      sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", sm: "flex-start" },
                        justifyContent: { xs: "flex-start", sm: "flex-start" },
                      }}
                    >
                      <Public
                        color={
                          isFullyOn
                            ? "success"
                            : isFullyOff
                            ? "error"
                            : "warning"
                        }
                      />
                      {isFullyOn
                        ? "All Cities Active"
                        : isFullyOff
                        ? "All Cities Inactive"
                        : "Partial Availability"}
                    </Typography>
                  </Tooltip>

                  <Button
                    variant="outlined"
                    color={isFullyOn ? "error" : "success"}
                    startIcon={isFullyOn ? <ToggleOff /> : <ToggleOn />}
                    onClick={handleGlobalToggle}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "20px",
                      px: 3,
                      py: 1,
                      width: { xs: "100%", sm: "auto" }, // Full width on mobile, auto on larger screens
                    }}
                  >
                    {isFullyOn ? "Disable All" : "Enable All"}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </CompactCard>

          {/* City Status Cards */}
          <CompactCard sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={800}>
                  City Availability
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {Object.values(cityStatus).filter((v) => v === "on").length}{" "}
                  of {Object.keys(cityStatus).length} active
                </Typography>
              </Box>

              <Grid container spacing={1}>
                {Object.entries(cityStatus).map(([city, status], index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor:
                          status === "on" ? "success.light" : "error.main",
                        backgroundColor:
                          status === "on" ? "success.50" : "background.paper",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {city.charAt(0).toUpperCase() + city.slice(1)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleCityToggle(city)}
                          sx={{
                            p: 0.5,
                            color:
                              status === "on" ? "success.main" : "error.main",
                          }}
                        >
                          {status === "on" ? (
                            <ToggleOn fontSize="small" />
                          ) : (
                            <ToggleOff fontSize="small" />
                          )}
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </CompactCard>

          {/* Product Image */}
          {/* {product.image_local_url && (
            <CompactCard sx={{ mb: 2, borderRadius: '12px', overflow: 'hidden' }}>
              <img 
                src={product.image_local_url} 
                alt={product.produt_name}
                style={{ width: '100%', height: '300px', objectFit: 'cover' }}
              />
            </CompactCard>
          )} */}

          {/* Deliverables Section */}
          <CompactCard sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("deliverables")}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    color: "text.primary",
                  }}
                >
                  {/* <CheckCircle color="primary" sx={{ mr: 1.5 }} /> */}
                  Deliverables
                </Typography>
                {expandedSections.deliverables ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </Box>

              <Collapse in={expandedSections.deliverables}>
                {/* <List dense sx={{ py: 0 }}>
                  {product.deliverables?.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: "32px" }}>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List> */}
                <List dense sx={{ py: 0 }}>
                  {product.deliverables?.map((item, index) => {
                    const isNote = item.toLowerCase().startsWith("note:");
                    return (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: "32px" }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <span
                              style={{ fontWeight: isNote ? "bold" : "normal" }}
                            >
                              {item}
                            </span>
                          }
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </CardContent>
          </CompactCard>

          {/* Requirements Section */}
          <CompactCard sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("requirements")}
              >
                <Typography variant="subtitle1" fontWeight={800}>
                  Client Requirements
                </Typography>
                {expandedSections.requirements ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </Box>

              <Collapse in={expandedSections.requirements}>
                <List dense sx={{ py: 0 }}>
                  {product.reqFromClient?.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: "32px" }}>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </CardContent>
          </CompactCard>
          {/* </Grid> */}

          {/* Sidebar Column */}
          {/* <Grid item xs={12} md={12}> */}
          <Box sx={{ position: "sticky", top: "16px" }}>
            {/* Pricing Section */}
            <CompactCard sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    mb: expandedSections.pricing ? 1 : 0,
                  }}
                  onClick={() => toggleSection("pricing")}
                >
                  <Typography variant="subtitle1" fontWeight={800}>
                    Pricing by City
                  </Typography>
                  {expandedSections.pricing ? <ExpandLess /> : <ExpandMore />}
                </Box>

                <Collapse in={expandedSections.pricing}>
                  {/* <Stack spacing={1}>
                    {product.price.map((locationPrice, index) => {
                      const [location, price] = Object.entries(locationPrice)[0];
                      const status = cityStatus[location] || 'off';
                      
                      return (
                        <Paper 
                          key={index} 
                          elevation={0} 
                          sx={{ 
                            p: 1.5, 
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOn
                              color={status === 'on' ? "primary" : "disabled"}
                              sx={{ mr: 1.5 }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              {location.charAt(0).toUpperCase() + location.slice(1)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ mr: 1 }}
                            >
                              <CurrencyRupee sx={{fontSize:"15px",textAlign:"center"}}/>{price.toLocaleString("en-IN")}
                            </Typography>
                            <StatusPill size="small" status={status} label={status.toUpperCase()} />
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack> */}
                  <Grid container spacing={2}>
                    {product.price.map((locationPrice, index) => {
                      const [location, price] =
                        Object.entries(locationPrice)[0];
                      return (
                        <Grid item xs={12} key={index}>
                          <PricingCard>
                            <CardContent
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                textAlign: "center",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <LocationOn
                                  color="primary"
                                  sx={{ mr: 0.1, fontSize: "1.5rem" }}
                                />
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, mr: 1 }}
                                  >
                                    {location.charAt(0).toUpperCase() +
                                      location.slice(1)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {/* <CurrencyRupee
                              sx={{ fontSize: "1.2em", mr: 0.5 }}
                            /> */}
                                <CurrencyRupee
                                  sx={{ fontSize: "15px", textAlign: "center" }}
                                />{" "}
                                {price.toLocaleString("en-IN")}
                              </Typography>
                            </CardContent>
                          </PricingCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Collapse>
              </CardContent>
            </CompactCard>
            {product.Customization &&
              product.Customization.map((data, index) => {
                // Skip index 0 if needed
                if (index === 0) return null;

                // Dynamically get the right price key
                const priceKey = `price${index}`; // e.g. "price1", "price2", "price3"
                const currentPrices = product[priceKey] || []; // fallback empty array
                const pricingKey = `pricing${index}`;
                const currentPricing = expandedSections[pricingKey] || false;
                return (
                  <CompactCard key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          mb: currentPricing ? 1 : 0,
                        }}
                        onClick={() => toggleSection(pricingKey)}
                      >
                        <Typography variant="subtitle1" fontWeight={800}>
                          Pricing for {data === "6*8" ? "6*8 Size" : data}
                        </Typography>
                        {currentPricing ? <ExpandLess /> : <ExpandMore />}
                      </Box>

                      <Collapse in={currentPricing}>
                        <Grid container spacing={2}>
                          {currentPrices.map((locationPrice, priceIndex) => {
                            const [location, price] =
                              Object.entries(locationPrice)[0];
                            return (
                              <Grid item xs={12} key={priceIndex}>
                                <PricingCard>
                                  <CardContent
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <LocationOn
                                        color="primary"
                                        sx={{ mr: 0.1, fontSize: "1.5rem" }}
                                      />
                                      <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, mr: 1 }}
                                      >
                                        {location.charAt(0).toUpperCase() +
                                          location.slice(1)}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        color: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <CurrencyRupee
                                        sx={{ fontSize: "15px" }}
                                      />
                                      {price.toLocaleString("en-IN")}
                                    </Typography>
                                  </CardContent>
                                </PricingCard>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Collapse>
                    </CardContent>
                  </CompactCard>
                );
              })}

            {/* Materials List */}
            <CompactCard sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("materials")}
                >
                  <Typography variant="subtitle1" fontWeight={800}>
                    Materials Required
                  </Typography>
                  {expandedSections.materials ? <ExpandLess /> : <ExpandMore />}
                </Box>

                <Collapse in={expandedSections.materials}>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                  >
                    {product.materials_list?.map((material, index) => (
                      <Chip
                        key={index}
                        label={material}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor: "primary.main",
                          backgroundColor: "",
                          color: "primary.dark",
                          fontWeight: 500,
                        }}
                        avatar={
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              width: 28,
                              height: 28,
                              color: "white",
                              fontSize: "0.875rem",
                            }}
                          >
                            {material[0].toUpperCase()}
                          </Avatar>
                        }
                      />
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </CompactCard>

            {/* Video Demo */}
            {product.videoLink && product.videoLink !== "no video" && (
              <CompactCard>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <PlayCircle color="primary" sx={{ mr: 1.5 }} />
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "text.primary",
                      }}
                    >
                      Video Demonstration
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    See this product in action
                  </Typography>
                  <GradientButton
                    fullWidth
                    startIcon={<PlayCircle />}
                    href={product.videoLink}
                    target="_blank"
                    rel="noopener"
                  >
                    CLick here to Watch Video & Output
                  </GradientButton>
                </CardContent>
              </CompactCard>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductDetailView;
